import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, Sparkles, MapPin, BadgeCheck } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { useT } from "@/context/LangContext";
import { useBrand } from "@/context/BrandContext";

/**
 * Bento-style hero: left panel = compact editorial text, right = asymmetric
 * mosaic of up to 6 partners with image-first tiles. Fills the first fold.
 */
export default function BentoPartnersHero({ partners, stats, onOpenPartner }) {
  const { t } = useT();
  const { brand } = useBrand();

  // Prefer featured > spotlight > rest, pick 6 unique partners
  const picks = pickBentoPartners(partners, 6);

  const heroSubtitle = t("mlp.hero.subtitle", {
    center: brand.center_short,
    n: partners.length,
    city: brand.city,
  });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 20% 10%, rgba(191,255,0,0.10), transparent 45%), linear-gradient(180deg, var(--brand-primary, #0b1220) 0%, var(--brand-primary-dark, #060a12) 100%)",
      }}
      data-testid="mlp-hero"
    >
      {/* subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="container-app relative py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
          {/* ============ LEFT PANEL: text/CTA ============ */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-5 text-white flex flex-col justify-between"
          >
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold mb-5 shadow-lg"
                style={{
                  background: "rgba(191,255,0,0.15)",
                  border: "1px solid rgba(191,255,0,0.3)",
                  color: "var(--brand-accent, #BFFF00)",
                }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                {t("mlp.hero.kicker", { center: brand.center_short })}
              </div>

              <h1
                className="font-heading font-black tracking-tighter leading-[0.95]"
                style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
                data-testid="mlp-hero-title"
              >
                <span className="block">{t("mlp.hero.title.1")}</span>
                <span className="block" style={{ color: "var(--brand-accent, #BFFF00)" }}>
                  {t("mlp.hero.title.2")}
                </span>
                <span className="block text-white/60">{t("mlp.hero.title.3")}</span>
              </h1>

              <p
                className="mt-5 text-sm md:text-base text-white/70 max-w-md leading-relaxed"
                data-testid="mlp-hero-subtitle"
              >
                {heroSubtitle}
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={() => scrollTo("counter")}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-black text-sm md:text-base transition-all shadow-2xl hover:shadow-lime-500/30 hover:-translate-y-0.5"
                  style={{ background: "var(--brand-accent, #BFFF00)", color: "var(--brand-primary, #0b1220)" }}
                  data-testid="mlp-hero-cta-primary"
                >
                  {t("mlp.hero.cta_primary")}
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollTo("partenaires")}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm md:text-base transition-all border border-white/20 hover:border-white/50 hover:bg-white/5 text-white"
                  data-testid="mlp-hero-cta-secondary"
                >
                  {t("mlp.hero.cta_secondary")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* STATS strip */}
            <div className="grid grid-cols-3 gap-2 mt-8 lg:mt-10">
              <MiniStat value={stats.partners} label={t("mlp.hero.stat.partners")} />
              <MiniStat
                value={stats.monthly}
                suffix={"€" + t("mlp.hero.stat.month_suffix")}
                label={t("mlp.hero.stat.saving")}
              />
              <MiniStat value={stats.categories} label={t("mlp.hero.stat.categories")} />
            </div>

            {/* location */}
            <div className="mt-5 inline-flex items-center gap-1.5 text-[10px] text-white/40 uppercase tracking-widest font-semibold">
              <MapPin className="w-3 h-3" />
              {brand.city}
            </div>
          </motion.div>

          {/* ============ RIGHT PANEL: bento mosaic ============ */}
          <div className="lg:col-span-7">
            <BentoWall picks={picks} onOpen={onOpenPartner} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ BENTO GRID ============ */
function BentoWall({ picks, onOpen }) {
  // 4 columns x 3 rows on desktop, asymmetric spans
  // Layout tiles: index → { colSpan, rowSpan, className }
  const layout = [
    // 0: big feature (col 1-2, row 1-2)
    { className: "col-span-2 row-span-2 min-h-[220px] md:min-h-[290px]" },
    // 1: tall right (col 3, row 1-2)
    { className: "col-span-2 md:col-span-1 row-span-2 min-h-[220px] md:min-h-[290px]" },
    // 2: small (col 4, row 1)
    { className: "col-span-1 md:col-span-1 row-span-1 min-h-[110px] md:min-h-[140px]" },
    // 3: small (col 4, row 2)
    { className: "col-span-1 md:col-span-1 row-span-1 min-h-[110px] md:min-h-[140px]" },
    // 4: wide bottom (col 1-2, row 3)
    { className: "col-span-2 row-span-1 min-h-[130px] md:min-h-[150px]" },
    // 5: wide bottom (col 3-4, row 3)
    { className: "col-span-2 row-span-1 min-h-[130px] md:min-h-[150px]" },
  ];

  return (
    <div
      className="grid grid-cols-4 gap-2.5 md:gap-3 h-full"
      style={{ gridAutoRows: "auto" }}
      data-testid="bento-wall"
    >
      {picks.map((partner, i) => (
        <BentoTile
          key={partner.id}
          partner={partner}
          index={i}
          className={layout[i]?.className || "col-span-2 row-span-1 min-h-[140px]"}
          onClick={() => onOpen && onOpen(partner)}
        />
      ))}
      {picks.length === 0 && <BentoPlaceholder />}
    </div>
  );
}

function BentoTile({ partner, className, index, onClick }) {
  const val = partner.offer_value || 0;
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.92, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.08, duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
      whileHover={{ scale: 1.02 }}
      className={`group relative rounded-2xl md:rounded-3xl overflow-hidden cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 ${className}`}
      data-testid={`bento-tile-${index}`}
      aria-label={`${partner.name} — ${partner.category}`}
    >
      {/* Image */}
      {partner.image_url && (
        <img
          src={resolveImageUrl(partner.image_url)}
          alt=""
          loading={index < 2 ? "eager" : "lazy"}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
      )}
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/25 to-transparent" />
      {/* Lime hover glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: "inset 0 0 60px 0 rgba(191,255,0,0.35)" }}
      />

      {/* Top-left badges */}
      <div className="absolute top-2.5 left-2.5 md:top-3 md:left-3 flex flex-wrap gap-1">
        {(partner.badges || []).slice(0, 1).map((b) => (
          <span key={b} className="px-2 py-0.5 rounded-full bg-white/95 text-navy-900 text-[9px] md:text-[10px] font-bold uppercase tracking-wider shadow-sm">
            {b}
          </span>
        ))}
        {partner.featured && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime-500 text-navy-900 text-[9px] md:text-[10px] font-black uppercase tracking-wider shadow-sm">
            <BadgeCheck className="w-2.5 h-2.5 md:w-3 md:h-3" />
          </span>
        )}
      </div>

      {/* Saving chip top-right */}
      {val > 0 && (
        <div
          className="absolute top-2.5 right-2.5 md:top-3 md:right-3 px-2 py-1 rounded-full text-[10px] md:text-xs font-black shadow-lg"
          style={{ background: "var(--brand-accent, #BFFF00)", color: "var(--brand-primary, #0b1220)" }}
        >
          −{val}€
        </div>
      )}

      {/* Bottom info */}
      <div className="absolute bottom-0 inset-x-0 p-3 md:p-4 text-white">
        <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-lime-400 font-bold mb-0.5 truncate">
          {partner.category}
        </div>
        <div className="font-heading font-black text-sm md:text-lg leading-tight drop-shadow line-clamp-2">
          {partner.name}
        </div>
      </div>
    </motion.button>
  );
}

function BentoPlaceholder() {
  return (
    <div className="col-span-4 row-span-3 min-h-[400px] rounded-3xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/40 text-sm">
      Les premiers partenaires arrivent bientôt.
    </div>
  );
}

function MiniStat({ value, label, suffix = "" }) {
  const [display, setDisplay] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="rounded-xl p-2.5 md:p-3 border border-white/10 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.03)" }}>
      <div className="font-heading font-black text-lg md:text-2xl leading-none" style={{ color: "var(--brand-accent, #BFFF00)" }}>
        {display}{suffix}
      </div>
      <div className="text-[9px] md:text-[10px] text-white/50 uppercase tracking-widest mt-1.5 font-semibold leading-tight">
        {label}
      </div>
    </div>
  );
}

function pickBentoPartners(partners, n) {
  if (!partners || partners.length === 0) return [];
  const sorted = [...partners].sort((a, b) => {
    // Priority: spotlight → featured → higher offer_value → recent
    const score = (p) => (p.spotlight ? 100 : 0) + (p.featured ? 50 : 0) + (p.offer_value || 0);
    return score(b) - score(a);
  });
  return sorted.slice(0, n);
}
